/**
 * types.ts
 *
 * Mirrors the backend's `/api/v1` contract from the plan
 * (i-want-to-build-linked-milner.md — "API surface", "SQLite schema",
 * "Filing engine" sections). Fields the plan states explicitly (error
 * envelope, EmailPayload, filing request/result, project search row) are
 * reproduced verbatim. A few response shapes the plan describes only in
 * prose (subscriptions, precheck, recent filings, /me) are filled in here
 * as a reasonable, explicitly-noted reading of that prose — the backend
 * agent may name fields slightly differently; adjust call sites in
 * api/client.ts + the hooks if so.
 */

// ---------------------------------------------------------------------------
// Error envelope
// ---------------------------------------------------------------------------

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    detail?: unknown;
  };
}

export class ApiError extends Error {
  readonly code: string;
  readonly detail: unknown;
  readonly status: number;

  constructor(status: number, code: string, message: string, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

// ---------------------------------------------------------------------------
// Email payload (collected client-side, sent as part of a filing request)
// ---------------------------------------------------------------------------

export interface EmailAddress {
  displayName: string;
  emailAddress: string;
}

export interface EmailAttachment {
  name: string;
  contentType: string;
  size: number;
  isInline: boolean;
  contentId: string | null;
  base64: string;
}

export interface EmailPayload {
  internetMessageId: string;
  subject: string;
  sentDateTime: string;
  from: EmailAddress;
  to: EmailAddress[];
  cc: EmailAddress[];
  headersRaw: string;
  htmlBody: string;
  attachments: EmailAttachment[];
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export interface DevLoginRequest {
  token: string;
}

export interface DevLoginResponse {
  jwt: string;
}

export interface MeResponse {
  username: string;
  email: string | null;
  needs_reauth: boolean;
}

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export interface ProjectSearchRow {
  id: number;
  project_code: string | null;
  name: string;
  division: string;
  /** Two-digit year bucket; null for Legacy-division projects (no year level). */
  year: string | null;
  path: string;
  subscribed: boolean;
  parse_ok: boolean;
}

export interface ProjectSearchResponse {
  results: ProjectSearchRow[];
}

/** A caller's saved search scope, sent as query params. Empty fields = no restriction. */
export interface SearchFilters {
  divisions: string[];
  years: string[];
  pathPrefix: string | null;
}

// ---------------------------------------------------------------------------
// Index status
// ---------------------------------------------------------------------------

/**
 * `demo` means the index was built by crawling the seeded fake tree, so a
 * zero-result search is expected for any real project name — the distinction
 * the picker's empty state needs in order to say something useful.
 */
export type IndexDataSource = "demo" | "imported" | "egnyte";

export interface IndexStatus {
  data_source: IndexDataSource;
  projects_active: number;
  last_indexed_at: string | null;
  last_status: string | null;
}

// ---------------------------------------------------------------------------
// Admin settings
// ---------------------------------------------------------------------------

export interface AdminSettings {
  egnyte_root: string;
  sync_divisions: string[];
  defaults: { egnyte_root: string; sync_divisions: string[] };
  /** Keys currently overriding their env-var default. */
  overridden: string[];
  storage_backend: string;
}

export interface UpdateAdminSettingsRequest {
  egnyte_root?: string;
  sync_divisions?: string[];
}

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

export interface Subscription {
  project_id: number;
  project_code: string | null;
  name: string;
  division: string;
  /** Two-digit year bucket; null for Legacy-division projects (no year level). */
  year: string | null;
  path: string;
  last_used_at: string | null;
  pin_order: number | null;
}

export interface SubscriptionListResponse {
  subscriptions: Subscription[];
}

export interface CreateSubscriptionRequest {
  project_id: number;
}

// ---------------------------------------------------------------------------
// Filings
// ---------------------------------------------------------------------------

export type ItemType = "email" | "rfi" | "submittal";

export interface FilingPrecheckRequest {
  internet_message_id: string;
  project_ids: number[];
}

export interface FilingPrecheckDuplicate {
  project_id: number;
  duplicate: boolean;
  filed_by: string | null;
  filed_at: string | null;
}

export interface FilingPrecheckResponse {
  results: FilingPrecheckDuplicate[];
}

export interface FilingRequest {
  client_request_id: string;
  item_type: ItemType;
  ref_number?: string;
  project_ids: number[];
  email: EmailPayload;
}

export type FilingFileKind = "eml" | "attachment";
export type FilingFileStatus = "uploaded" | "failed" | "deleted";

export interface FilingFileResult {
  kind: FilingFileKind;
  filename: string;
  status: FilingFileStatus;
  error?: string;
}

export type FilingTargetResultStatus = "filed" | "partial" | "failed";

export interface FilingTargetResult {
  project_id: number;
  dest_path: string;
  status: FilingTargetResultStatus;
  duplicate: boolean;
  files: FilingFileResult[];
}

export interface FilingGroupResult {
  group_id: string;
  targets: FilingTargetResult[];
}

/** Fuller status union for a filing_target as it can appear later (e.g. in
 * the recent-filings list), including states that never appear in the
 * immediate synchronous POST /filings response. */
export type FilingTargetStatus =
  | "pending"
  | "filed"
  | "partial"
  | "failed"
  | "undone";

export interface RecentFilingTarget {
  target_id: number;
  filing_id: string;
  subject: string;
  item_type: ItemType;
  ref_number: string | null;
  project_id: number;
  project_code: string | null;
  project_name: string;
  dest_path: string;
  status: FilingTargetStatus;
  filed_at: string;
  can_undo: boolean;
}

export interface RecentFilingsResponse {
  filings: RecentFilingTarget[];
}

export interface UndoFilingTargetResponse {
  target_id: number;
  status: FilingTargetStatus;
}
